/*
 * Shrine on Horizon — section widgets.
 * Hotspots, parallax hero, content tabs, Instagram-style stories, contact form validation,
 * page scroll progress, date countdowns, animated results, Lottie animations, infinite
 * product loading, promo popup, comparison slider, section grouping and product recommendations.
 * Depends on helpers from sh-global.js. Safe to include more than once.
 */
(() => {
  if (customElements.get('hotspot-button')) return;

  /* ---------- Hotspots ---------- */

  const hotspotButtons = new Set();
  document.addEventListener('mousedown', (event) => {
    hotspotButtons.forEach((button) => {
      if (button.dataset.open === 'true' && !button.contains(event.target)) button.closeModal();
    });
  });

  class HotspotButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.hotspot-btn');
      this.content = this.querySelector('.hotspot__content');
      this.openEvent = this.dataset.openEvent;
      this.mobileOverlay = this.querySelector('.hotspot-overlay');
      if (!this.button || !this.content) return;
      this.button.addEventListener('click', this.toggleModal.bind(this));
      if (
        this.openEvent === 'hover' &&
        window.matchMedia('(hover: hover)').matches &&
        window.matchMedia('(pointer: fine)').matches
      ) {
        this.button.addEventListener('mouseover', this.openModal.bind(this));
        this.button.addEventListener('mouseout', this.startCheckingMouseLeave.bind(this));
        this.content.addEventListener('mouseover', this.stopCheckingMouseLeave.bind(this));
        this.content.addEventListener('mouseout', this.startCheckingMouseLeave.bind(this));
      }
      this.mobileOverlay?.addEventListener('click', this.closeModal.bind(this));
    }

    connectedCallback() {
      hotspotButtons.add(this);
    }

    disconnectedCallback() {
      hotspotButtons.delete(this);
    }

    startCheckingMouseLeave() {
      this.checkingMouseLeave = true;
      setTimeout(() => {
        if (this.checkingMouseLeave) this.closeModal();
      }, 200);
    }

    stopCheckingMouseLeave() {
      this.checkingMouseLeave = false;
    }

    toggleModal() {
      if (this.dataset.open != 'true') this.openModal();
    }

    openModal() {
      const header = document.querySelector('.section-header');
      const stickyHeader = document.querySelector('sticky-header');
      let headerHeight = header ? header.clientHeight : 0;
      if (!header || header.classList.contains('shopify-section-header-hidden') || !stickyHeader) headerHeight = 0;
      if (this.content.getBoundingClientRect().top - headerHeight < 0) this.dataset.direction = 'bottom';
      this.dataset.open = 'true';
    }

    closeModal() {
      this.dataset.open = 'false';
      setTimeout(() => (this.dataset.direction = ''), 100);
    }
  }
  customElements.define('hotspot-button', HotspotButton);

  /* ---------- Parallax hero ---------- */

  class ParallaxHero extends HTMLElement {
    connectedCallback() {
      this.layers = this.querySelectorAll('.parallax-hero__layer');
      this.animateOnEnter = this.dataset.animationStart === 'bottom';
      this.onScroll = () => requestAnimationFrame(this.handleScroll.bind(this));
      this.handleScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    handleScroll() {
      const { top, height } = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      if (top > viewport || top + height < 0) return;
      let progress;
      if (this.animateOnEnter) {
        progress = Math.min((viewport - top) / (viewport + height), 1);
      } else {
        const scrolled = top >= 0 ? 0 : -top;
        if (scrolled > height) return;
        progress = Math.min(scrolled / height, 1);
      }
      this.layers.forEach((layer) => {
        const y = top * (parseInt(layer.dataset.scrollY) / 100);
        const x = parseInt(layer.dataset.scrollX) * progress;
        const zoom = 100 + (parseInt(layer.dataset.zoom) - 100) * progress;
        const rotation = parseInt(layer.dataset.rotation) * 3.6 * progress;
        layer.style.transform = `translateY(${y}px) translateX(${x}%) scale(${zoom / 100}) rotate(${rotation}deg)`;
      });
    }
  }
  customElements.define('parallax-hero', ParallaxHero);

  /* ---------- Content tabs ---------- */

  class ContentTabs extends HTMLElement {
    constructor() {
      super();
      this.activeButtonClass = this.dataset.productTabs == 'true' ? 'active' : 'content-tab-button--active';
      this.buttons = this.querySelectorAll('.content-tab-button-js');
      this.tabs = this.querySelectorAll('.content-tab');
      this.tabsContainer = this.querySelector('.content-tabs__tabs-js');
      this.activeButton = this.querySelector(`.${this.activeButtonClass}`);
      this.activeTab = this.querySelector('.content-tab--active');
      this.isMovingAnimation = this.dataset.animation === 'moving';
      if (this.isMovingAnimation) {
        this.activeBg = this.querySelector('.content-tab-buttom__active-bg');
        this.handleActiveBg();
        if (this.activeBg) this.activeBg.style.transitionDuration = '0.4s';
      }
      this.setHeight();
      this.buttons.forEach((button) => button.addEventListener('click', this.handleClick.bind(this)));
      window.addEventListener('resize', () => {
        this.setHeight();
        if (this.isMovingAnimation) this.handleActiveBg();
      });
    }

    handleClick(event) {
      this.activeButton = event.currentTarget;
      this.buttons.forEach((button) => button.classList.remove(this.activeButtonClass));
      this.activeButton.classList.add(this.activeButtonClass);
      this.tabs.forEach((tab) => {
        tab.classList.remove('content-tab--active');
        if (tab.dataset.index === this.activeButton.dataset.index) this.activeTab = tab;
      });
      this.activeTab.classList.add('content-tab--active');
      this.setHeight();
      if (this.isMovingAnimation) this.handleActiveBg();
    }

    setHeight() {
      if (this.activeTab && this.tabsContainer) this.tabsContainer.style.height = `${this.activeTab.clientHeight}px`;
    }

    handleActiveBg() {
      if (!this.activeBg || !this.activeButton) return;
      const rect = this.activeButton.getBoundingClientRect();
      this.activeBg.style.width = `${rect.width}px`;
      this.activeBg.style.height = `${rect.height}px`;
      this.activeBg.style.top = `${this.activeButton.offsetTop}px`;
      this.activeBg.style.left = `${this.activeButton.offsetLeft}px`;
    }
  }
  customElements.define('content-tabs', ContentTabs);

  /* ---------- Insta stories ---------- */

  class InstaStories extends HTMLElement {
    constructor() {
      super();
      this.openButtons = this.querySelectorAll('.insta-story-open-btn');
      this.openButtonsOverflowContainer = this.querySelector('.insta-stories__open-buttons-container');
      this.openButtonsContainer = this.querySelector('.insta-stories__open-buttons');
      this.openBtnsPrev = this.querySelector('.insta-stories__open-btns-prev');
      this.openBtnsNext = this.querySelector('.insta-stories__open-btns-next');
      this.closeButtons = this.querySelectorAll('.insta-stories__close-button');
      this.modal = this.querySelector('.insta-stories__modal');
      this.modalOpen = false;
      this.slider = this.querySelector('.insta-stories__slider');
      this.stories = this.querySelectorAll('.insta-story');
      this.activeIndex = 0;
      this.activeStory = this.stories[this.activeIndex];
      this.lastIndex = parseInt(this.dataset.lastIndex);
      this.pauseResumeBtns = this.querySelectorAll('.insta-story__pause-resume-btn');
      this.isPaused = false;
      this.volumeBtns = this.querySelectorAll('.insta-story__volume-btn');
      this.isMuted = true;
      if (!this.modal || !this.slider) return;

      this.initStories();
      this.initButtonsSlider();

      this.openButtons.forEach((button) => button.addEventListener('click', this.openModal.bind(this)));
      this.closeButtons.forEach((button) => button.addEventListener('click', this.closeModal.bind(this)));
      this.querySelectorAll('.insta-story__prev').forEach((button) =>
        button.addEventListener('click', () => this.storyPrevBtnClick())
      );
      this.querySelectorAll('.insta-story__next').forEach((button) =>
        button.addEventListener('click', () => this.storyNextBtnClick())
      );
      this.querySelectorAll('.insta-story__slide-btn').forEach((button) =>
        button.addEventListener('click', (event) => this.changeActiveStory(parseInt(event.currentTarget.dataset.index)))
      );
      this.pauseResumeBtns.forEach((button) => button.addEventListener('click', this.togglePauseResume.bind(this)));
      this.volumeBtns.forEach((button) => button.addEventListener('click', this.toggleIsMuted.bind(this)));
      this.slider.addEventListener('touchstart', (event) => (this.touchStartX = event.touches[0].clientX), {
        passive: true,
      });
      this.slider.addEventListener('touchend', this.touchEndHandler.bind(this));

      document.addEventListener('keydown', (event) => {
        if (!this.modalOpen) return;
        if (event.key === 'Escape') this.closeModal();
        if (event.key === 'ArrowLeft') this.storyPrevBtnClick();
        if (event.key === 'ArrowRight') this.storyNextBtnClick();
      });
    }

    mediaAt(story, index) {
      return story.querySelectorAll('.insta-story__media')[index];
    }

    autoplay() {
      const story = this.activeStory;
      const index = parseInt(story.dataset.activeMediaIndex);
      const media = this.mediaAt(story, index);
      const duration = parseInt(media.getAttribute('data-duration')) * 1000;
      this.updateProgressBars(index);
      this.autoplayStartTime = Date.now();
      this.autoplayTimeout = setTimeout(() => this.storyNextBtnClick(), duration);
    }

    storyPrevBtnClick() {
      const story = this.activeStory;
      if (story.dataset.activeMediaIndex === '0') {
        this.changeActiveStory(this.activeIndex - 1);
      } else {
        this.changeActiveMedia(parseInt(story.dataset.activeMediaIndex) - 1);
      }
    }

    storyNextBtnClick() {
      const story = this.activeStory;
      if (story.dataset.activeMediaIndex === story.dataset.lastMediaIndex) {
        this.changeActiveStory(this.activeIndex + 1);
      } else {
        this.changeActiveMedia(parseInt(story.dataset.activeMediaIndex) + 1);
      }
    }

    changeActiveStory(index) {
      clearTimeout(this.autoplayTimeout);
      if (index > this.lastIndex || index < 0) return;

      const previous = this.stories[this.activeIndex];
      previous.classList.remove('internal-video--loading');
      const previousMediaIndex = parseInt(previous.dataset.activeMediaIndex);
      if (previous.dataset.played === 'true' || previousMediaIndex > 0) {
        const progress = previous.querySelectorAll('.insta-story__progress-item')[previousMediaIndex];
        if (progress) {
          progress.classList.remove('insta-story__progress-item--active');
          if (index > this.activeIndex) progress.classList.add('insta-story__progress-item--completed');
        }
      }
      const previousMedia = this.mediaAt(previous, previousMediaIndex);
      if (previousMedia?.getAttribute('data-type') === 'video') {
        const video = previousMedia.querySelector('video');
        if (video) {
          video.pause();
          if (!this.isPaused || this.bufferPaused) video.currentTime = 0;
        }
      }

      this.activeIndex = index;
      this.activeStory = this.stories[index];
      this.stories.forEach((story) => story.classList.remove('insta-story--active'));
      this.activeStory.classList.add('insta-story--active');
      this.activeStory.classList.remove('internal-video--loading');
      this.activeStory.dataset.played = 'true';
      this.slider.style.transform = `translateX(calc(var(--story-width) * ${index * (isRtl ? 1 : -1)}))`;
      const mediaIndex = parseInt(this.activeStory.dataset.activeMediaIndex, 10);
      this.changeActiveMedia(mediaIndex);
      this.updateProgressBars(mediaIndex);
    }

    changeActiveMedia(index) {
      if (this.bufferPaused && this.isPaused) this.resumeStory();
      clearTimeout(this.autoplayTimeout);
      if (index < 0) return;
      const story = this.activeStory;
      const lastIndex = parseInt(story.dataset.lastMediaIndex);
      if (index > lastIndex) return;

      if (this.currentVideoEl) {
        this.currentVideoEl.removeEventListener('waiting', this._onBuffer);
        this.currentVideoEl.removeEventListener('playing', this._onResume);
        this.currentVideoEl = null;
      }

      story.dataset.activeMediaIndex = index;
      story.querySelectorAll('.insta-story__media').forEach((media, i) => {
        const video = media.querySelector('video');
        if (i === index) {
          media.style.display = 'block';
          if (media.getAttribute('data-type') === 'video' && video) {
            if (!this.isPaused) video.play();
            this._onBuffer = () => {
              if (!this.isPaused) this.pauseStory(true);
              this.bufferPaused = true;
              story.classList.add('internal-video--loading');
            };
            this._onResume = () => {
              if (this.isPaused) this.resumeStory();
              this.bufferPaused = false;
              story.classList.remove('internal-video--loading');
            };
            video.addEventListener('waiting', this._onBuffer);
            video.addEventListener('playing', this._onResume);
            this.currentVideoEl = video;
          }
          const posted = story.querySelector('.insta-story__time-posted');
          if (posted) posted.innerHTML = media.dataset.timePosted;
        } else {
          media.style.display = '';
          if (media.getAttribute('data-type') === 'video' && video) {
            video.pause();
            if (!this.isPaused || this.bufferPaused) video.currentTime = 0;
          }
        }
      });

      this.updateProgressBars(index);
      if (!this.isPaused) this.autoplay();
      story
        .querySelectorAll('.insta-story__prev')
        .forEach((button) => button.toggleAttribute('disabled', this.activeIndex === 0 && index === 0));
      story
        .querySelectorAll('.insta-story__next')
        .forEach((button) => button.toggleAttribute('disabled', this.activeIndex === this.lastIndex && index === lastIndex));
    }

    updateProgressBars(index) {
      this.activeStory.querySelectorAll('.insta-story__progress-item').forEach((item, i) => {
        item.classList.remove('insta-story__progress-item--completed', 'insta-story__progress-item--active');
        if (i < index) item.classList.add('insta-story__progress-item--completed');
        if (i === index) item.classList.add('insta-story__progress-item--active');
      });
    }

    openModal(event) {
      window.scrollBy(0, -1);
      this.modal.dataset.open = 'true';
      this.modalOpen = true;
      document.body.classList.add('overflow-hidden');
      this.changeActiveStory(parseInt(event.currentTarget.dataset.index));
    }

    closeModal() {
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.modal.dataset.open = 'false';
      this.modalOpen = false;
      document.body.classList.remove('overflow-hidden');
      clearTimeout(this.autoplayTimeout);
    }

    initButtonsSlider() {
      const overflow = this.openButtonsOverflowContainer;
      const container = this.openButtonsContainer;
      if (!overflow || !container || !this.openButtons.length) return;
      if (overflow.clientWidth > container.clientWidth) return;

      const isTouch = () => 'ontouchstart' in window || navigator.maxTouchPoints;
      let startX;
      let currentX = 0;
      let lastX = 0;
      const setX = (x) => {
        container.style.transform = `translateX(${x}px)`;
        currentX = x;
      };
      overflow.addEventListener(
        'touchstart',
        (event) => {
          this.isDragging = true;
          startX = event.touches[0].clientX - lastX;
        },
        { passive: true }
      );
      overflow.addEventListener(
        'touchmove',
        (event) => {
          if (!this.isDragging) return;
          const x = event.touches[0].clientX - startX;
          if (x < 0 && Math.abs(x) <= container.offsetWidth - overflow.offsetWidth) setX(x);
        },
        { passive: true }
      );
      overflow.addEventListener('touchend', () => {
        this.isDragging = false;
        lastX = currentX;
      });

      const buttonWidth = parseFloat(getComputedStyle(this.openButtons[0]).width);
      const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
      const pageWidth = () => (Math.floor(overflow.clientWidth / (buttonWidth + gap)) - 1) * (buttonWidth + gap);
      const direction = isRtl ? -1 : 1;
      const updateArrows = () => {
        const scrolled = overflow.scrollLeft * direction;
        this.openBtnsPrev.style.display = scrolled <= 0 ? 'none' : 'grid';
        this.openBtnsNext.style.display = scrolled > overflow.scrollWidth - container.clientWidth ? 'none' : 'grid';
      };
      const hideArrows = () => {
        this.openBtnsPrev.style.display = 'none';
        this.openBtnsNext.style.display = 'none';
      };
      if (!this.openBtnsPrev || !this.openBtnsNext) return;

      this.openBtnsPrev.addEventListener('click', () => {
        overflow.scrollBy({ left: -pageWidth() * direction, behavior: 'smooth' });
        setTimeout(updateArrows, 300);
      });
      this.openBtnsNext.addEventListener('click', () => {
        overflow.scrollBy({ left: pageWidth() * direction, behavior: 'smooth' });
        setTimeout(updateArrows, 300);
      });
      updateArrows();
      if (isTouch()) hideArrows();

      window.addEventListener(
        'resize',
        debounce(() => {
          if (overflow.clientWidth <= container.clientWidth) {
            isTouch() ? hideArrows() : updateArrows();
          } else {
            hideArrows();
          }
        }, 250)
      );
    }

    initStories() {
      this.stories.forEach((story) => {
        const media = story.querySelectorAll('.insta-story__media');
        story.dataset.lastMediaIndex = media.length - 1;
        story.dataset.played = 'false';
        const progress = story.querySelector('.insta-story__progress');
        if (progress) {
          progress.innerHTML = '';
          media.forEach((item) => {
            const progressItem = document.createElement('span');
            progressItem.className = 'insta-story__progress-item';
            progressItem.style.setProperty('--duration', `${item.getAttribute('data-duration')}s`);
            const bar = document.createElement('span');
            bar.className = 'insta-story__progress-bar';
            progressItem.appendChild(bar);
            progress.appendChild(progressItem);
          });
        }
        const active = media[parseInt(story.dataset.activeMediaIndex)];
        if (active) active.style.display = 'block';
      });
    }

    togglePauseResume(event) {
      const paused = event.currentTarget.getAttribute('data-paused') === 'true';
      paused ? this.resumeStory() : this.pauseStory();
      this.pauseResumeBtns.forEach((button) => button.setAttribute('data-paused', paused ? 'false' : 'true'));
    }

    pauseStory(fromBuffer = false) {
      this.isPaused = true;
      const media = this.mediaAt(this.activeStory, parseInt(this.activeStory.dataset.activeMediaIndex));
      const duration = parseInt(media.getAttribute('data-duration')) * 1000;
      this.remainingTime = duration - (Date.now() - this.autoplayStartTime);
      clearTimeout(this.autoplayTimeout);
      if (media.getAttribute('data-type') === 'video' && !fromBuffer) media.querySelector('video')?.pause();
      this.querySelectorAll('.insta-story__progress-bar').forEach((bar) => (bar.style.animationPlayState = 'paused'));
    }

    resumeStory() {
      this.isPaused = false;
      this.querySelectorAll('.insta-story__progress-bar').forEach((bar) => (bar.style.animationPlayState = 'running'));
      const media = this.mediaAt(this.activeStory, parseInt(this.activeStory.dataset.activeMediaIndex));
      if (media.getAttribute('data-type') === 'video') media.querySelector('video')?.play();
      this.autoplayStartTime = Date.now();
      this.autoplayTimeout = setTimeout(() => this.storyNextBtnClick(), this.remainingTime);
    }

    toggleIsMuted() {
      this.isMuted = !this.isMuted;
      this.querySelectorAll('video').forEach((video) => (video.muted = this.isMuted));
      this.volumeBtns.forEach((button) => button.setAttribute('data-muted', this.isMuted ? 'true' : 'false'));
    }

    touchEndHandler(event) {
      const delta = event.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(delta) < 50) return;
      const direction = isRtl ? -1 : 1;
      if (delta < 0) {
        if (this.activeIndex < this.lastIndex) this.changeActiveStory(this.activeIndex + direction);
      } else if (this.activeIndex > 0) {
        this.changeActiveStory(this.activeIndex - direction);
      }
    }
  }
  customElements.define('insta-stories', InstaStories);

  /* ---------- Contact form validation ---------- */

  class ContactForm extends HTMLElement {
    constructor() {
      super();
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleFieldInput = this.handleFieldInput.bind(this);
      this.button = this.querySelector('.button');
      this.tnc = this.querySelector('.tnc-checkbox');
      this.requiredFields = [];
    }

    fieldInput(wrapper) {
      return wrapper.querySelector('.field__input, .text-area, .select__select');
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.requiredFields = Array.from(this.querySelectorAll('.field-wrapper[data-required="true"]'));
      if (this.requiredFields.length === 0 && !this.tnc) return;
      this.form.addEventListener('submit', this.handleFormSubmit);
      this.requiredFields.forEach((wrapper) => this.fieldInput(wrapper)?.addEventListener('input', this.handleFieldInput));
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleFormSubmit);
      this.requiredFields.forEach((wrapper) => this.fieldInput(wrapper)?.removeEventListener('input', this.handleFieldInput));
    }

    handleFormSubmit(event) {
      if (this.button) {
        this.button.disabled = true;
        this.button.classList.add('loading');
      }
      let valid = true;
      if (this.tnc && this.tnc.dataset.checked !== 'true') {
        valid = false;
        document
          .querySelector(`.tnc-checkbox-warning--${this.tnc.dataset.warningPosition}-${this.tnc.dataset.section}`)
          ?.classList.remove('hidden');
      }
      this.requiredFields.forEach((wrapper) => {
        const input = this.fieldInput(wrapper);
        if (input && !input.value.trim()) {
          valid = false;
          wrapper.classList.add('field-wrapper--error');
          wrapper.querySelector('.field-wrapper__error-msg')?.classList.remove('hidden');
        }
      });
      if (valid) return;
      if (this.button) {
        this.button.disabled = false;
        this.button.classList.remove('loading');
      }
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    handleFieldInput(event) {
      const wrapper = event.target.closest('.field-wrapper');
      wrapper.classList.remove('field-wrapper--error');
      wrapper.querySelector('.field-wrapper__error-msg')?.classList.add('hidden');
    }
  }
  customElements.define('contact-form', ContactForm);

  /* ---------- Page scroll progress ---------- */

  class PageScrollProgress extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('.page-progress__fill');
      this.update = this.update.bind(this);
      window.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.update);
      window.removeEventListener('resize', this.update);
    }

    update() {
      if (!this.fill) return;
      const root = document.documentElement;
      const scrolled = window.pageYOffset || root.scrollTop;
      const max = root.scrollHeight - root.clientHeight;
      this.fill.style.width = `${max > 0 ? Math.round((scrolled / max) * 100) : 0}%`;
    }
  }
  customElements.define('page-scroll-progress', PageScrollProgress);

  /* ---------- Date countdown ---------- */

  class DateCountdownTimer extends HTMLElement {
    connectedCallback() {
      this.update = this.update.bind(this);
      this.wrapper = this.closest('.countdown-wrapper');
      if (!this.wrapper) return;
      const data = this.wrapper.dataset;
      this.behavior = data.endBehavior;
      this.repeatHours = parseInt(data.repeatHours) || 24;
      this.labels = { days: data.labelDays, hours: data.labelHours, minutes: data.labelMinutes, seconds: data.labelSeconds };
      this.units = {
        days: data.showDays === 'true',
        hours: data.showHours === 'true',
        minutes: data.showMinutes === 'true',
        seconds: data.showSeconds === 'true',
      };
      this.timerStyle = this.dataset.style;
      this.endTime = this.parseEndTime((data.endTime || '').trim(), data.timezone, parseInt(data.utcOffset) || 0);
      if (!(this.endTime instanceof Date) || isNaN(this.endTime.getTime())) {
        this.wrapper.style.display = 'none';
        return;
      }
      this.timer = setInterval(this.update, 1000);
      this.update();
    }

    parseEndTime(value, timezone, utcOffset) {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (!match) return new Date(value.replace(/-/g, '/'));
      const [year, month, day, hour, minute, second] = match.slice(1).map((part, i) => parseInt(part || '0', 10) - (i === 1 ? 1 : 0));
      if (timezone !== 'fixed') return new Date(year, month, day, hour, minute, second);
      return new Date(Date.UTC(year, month, day, hour, minute, second) - utcOffset * 3600000);
    }

    update() {
      const now = new Date();
      let remaining = Math.floor((this.endTime - now) / 1000);
      if (!isFinite(remaining)) return;
      if (remaining <= 0) {
        if (this.behavior === 'hide') {
          this.wrapper.style.display = 'none';
          clearInterval(this.timer);
          return;
        }
        if (this.behavior === 'restart') {
          let next = new Date(this.endTime);
          while (next <= now) next = new Date(next.getTime() + this.repeatHours * 3600000);
          this.endTime = next;
        } else if (this.behavior === 'next_day') {
          const next = new Date(this.endTime);
          while (next <= now) next.setDate(next.getDate() + 1);
          this.endTime = next;
        }
        remaining = Math.max(0, Math.floor((this.endTime - now) / 1000));
      }
      const values = {
        days: Math.floor(remaining / 86400),
        hours: Math.floor((remaining % 86400) / 3600),
        minutes: Math.floor((remaining % 3600) / 60),
        seconds: remaining % 60,
      };
      const ariaNames = { days: 'Day', hours: 'Hour', minutes: 'Minute', seconds: 'Second' };
      Object.keys(this.units).forEach((unit) => {
        if (!this.units[unit]) return;
        const number = this.querySelector(`[aria-label="${ariaNames[unit]}"] .countdown-timer__number`);
        if (!number) return;
        const text = String(values[unit]).padStart(2, '0');
        if (this.timerStyle === '4') {
          number.innerHTML = '';
          text.split('').forEach((digit) => {
            const span = document.createElement('span');
            span.className = 'countdown-timer__digit';
            span.textContent = digit;
            number.appendChild(span);
          });
        } else {
          number.textContent = text;
        }
        number.setAttribute('aria-label', `${values[unit]} ${this.labels[unit]}`);
      });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('date-countdown-timer', DateCountdownTimer);

  /* ---------- Animated results ---------- */

  class ResultsContainer extends HTMLElement {
    connectedCallback() {
      this.styleType = this.dataset.style;
      this.duration = parseInt(this.dataset.duration, 10) || 650;
      this.initDelay = parseInt(this.dataset.delay, 10) || 0;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          setTimeout(() => this.animateAll(), this.initDelay);
          this.observer.disconnect();
        },
        { threshold: 0.3 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    animateAll() {
      this.querySelectorAll('.results__percentage').forEach((row) =>
        this.animateRow(row, parseInt(row.dataset.percentage, 10) || 0)
      );
    }

    animateRow(row, percentage) {
      const label = row.querySelector('p');
      const start = performance.now();
      if (this.styleType === 'circle') {
        const ring = row.querySelector('.ring__prog');
        if (ring) requestAnimationFrame(() => ring.setAttribute('stroke-dasharray', `${percentage} 100`));
      }
      const step = (time) => {
        const progress = Math.min((time - start) / this.duration, 1);
        if (label) label.textContent = `${Math.round(progress * percentage)}%`;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }
  customElements.define('results-container', ResultsContainer);

  /* ---------- Lottie animation ---------- */

  class JsonAnimation extends HTMLElement {
    constructor() {
      super();
      this.animation = null;
      this.currentUrl = null;
      this.observer = null;
      this._onResize = this._onResize.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      if (!window.lottie?.loadAnimation) {
        // lottie-web is loaded (deferred) by the block/section that renders this element.
        this._initialized = false;
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => this.connectedCallback(), { once: true });
        } else {
          console.warn('<json-animation> requires lottie-web to be loaded.');
        }
        return;
      }
      this.desktopUrl = this.getAttribute('animation-url') || this.dataset.animationUrl || '';
      this.mobileUrl = this.getAttribute('animation-url-mobile') || this.dataset.animationUrlMobile || '';
      if (!this.desktopUrl) return;
      this.loop = this._getBoolAttr('loop', true);
      this.autoplay = this._getBoolAttr('autoplay', true);
      this.clickToPlay = this._getBoolAttr('click-to-play', false);
      this.speed = parseFloat(this.getAttribute('speed') || this.dataset.speed) || 1;
      if (this.clickToPlay) {
        this.style.cursor = 'pointer';
        this.addEventListener('click', this._onClick);
      }
      this._loadAnimation();
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this.removeEventListener('click', this._onClick);
      this.observer?.disconnect();
      this.observer = null;
      this.animation?.destroy();
      this.animation = null;
      this._initialized = false;
      clearTimeout(this.resizeTimeout);
    }

    _getBoolAttr(name, fallback) {
      if (!this.hasAttribute(name)) return fallback;
      const value = this.getAttribute(name);
      return value === '' || value === 'true' || value === '1';
    }

    _getAnimationUrl() {
      return window.innerWidth < 750 && this.mobileUrl ? this.mobileUrl : this.desktopUrl;
    }

    _onResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this._loadAnimation(), 250);
    }

    _onClick() {
      if (!this.animation) return;
      this.animation.isPaused ? this.animation.play() : this.animation.pause();
    }

    _setupObserver() {
      this.observer?.disconnect();
      this.observer = null;
      if (this.autoplay) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!this.animation) return;
            if (entry.isIntersecting) {
              this.animation.play();
              if (!this.loop) this.observer.unobserve(this);
            } else if (this.loop) {
              this.animation.pause();
            }
          });
        },
        { threshold: 0.3 }
      );
      this.observer.observe(this);
    }

    _loadAnimation() {
      const url = this._getAnimationUrl();
      if (!url || (this.currentUrl === url && this.animation)) return;
      this.currentUrl = url;
      this.animation?.destroy();
      this.innerHTML = '';
      this.animation = window.lottie.loadAnimation({
        container: this,
        renderer: 'svg',
        loop: this.loop,
        autoplay: this.autoplay,
        path: url,
      });
      this.animation.setSpeed(this.speed);
      this._setupObserver();
    }
  }
  customElements.define('json-animation', JsonAnimation);

  /* ---------- Infinite product loading ---------- */

  class InfiniteProducts extends HTMLElement {
    connectedCallback() {
      this.sentinel = this.querySelector('[data-infinite-sentinel]');
      this.loading = false;
      if (!this.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.loading) this.loadMore();
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async loadMore() {
      const url = this.sentinel.dataset.nextUrl;
      const grid = document.getElementById('product-grid');
      if (!url || !grid) return;
      this.loading = true;
      this.sentinel.classList.add('infinite-sentinel--loading');
      try {
        const response = await fetch(url);
        if (!response.ok) throw Error(`Request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        html.getElementById('product-grid')?.querySelectorAll(':scope > li').forEach((item) => grid.appendChild(item));
        const nextUrl = html.querySelector('[data-infinite-sentinel]')?.dataset.nextUrl;
        if (nextUrl) {
          this.sentinel.dataset.nextUrl = nextUrl;
        } else {
          this.observer.disconnect();
          this.sentinel.remove();
          this.sentinel = null;
        }
      } catch (error) {
        console.error('Infinite load failed:', error);
      } finally {
        this.loading = false;
        this.sentinel?.classList.remove('infinite-sentinel--loading');
      }
    }
  }
  customElements.define('infinite-products', InfiniteProducts);

  /* ---------- Promo popup ---------- */

  class PromoPopup extends HTMLElement {
    constructor() {
      super();
      this.testMode = this.dataset.testMode === 'true';
      this.secondsDelay = this.dataset.delaySeconds;
      this.daysFrequency = this.dataset.delayDays;
      this.modal = this.querySelector('.sign-up-popup-modal');
      this.timer = this.querySelector('.popup-modal__timer');
      this.timerDuration = this.dataset.timerDuration;
      this.closeBtns = this.querySelectorAll('.promp-popup__close-btn');
      this.overlay = document.querySelector('.sign-up-popup-overlay');
      this.storageKey = `promo-bar-data-${window.location.host}`;
      if (!this.modal) return;

      if (!this.testMode) {
        const stored = this.readStorage();
        if (stored === null || new Date().getTime() > new Date(stored.next_display_date).getTime()) {
          this.openPopupModal();
        }
      } else if (this.timer) {
        this.displayPromoTimer();
      }
      this.closeBtns.forEach((button) => button.addEventListener('click', this.closeModal.bind(this)));
    }

    readStorage() {
      try {
        const value = localStorage.getItem(this.storageKey);
        return value === null ? null : JSON.parse(value);
      } catch (error) {
        return null;
      }
    }

    openPopupModal() {
      setTimeout(() => {
        this.modal.classList.add('popup-modal--active');
        this.overlay?.classList.add('popup-overlay--active');
        const nextDisplay = addDays(new Date(), parseInt(this.daysFrequency));
        try {
          localStorage.setItem(this.storageKey, JSON.stringify({ next_display_date: nextDisplay, dismissed: false }));
        } catch (error) {
          /* storage unavailable */
        }
        if (this.timer) this.displayPromoTimer();
      }, parseInt(this.secondsDelay) * 1000 + 3000);
    }

    displayPromoTimer() {
      this.minutesSpan = this.querySelector('.popup-modal__timer__minutes');
      this.secondsSpan = this.querySelector('.popup-modal__timer__seconds');
      this.totalSeconds = parseFloat(this.timerDuration) * 60;
      this.updateTimer();
      clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        this.totalSeconds -= 1;
        this.updateTimer();
        if (this.totalSeconds <= 0) this.totalSeconds = parseFloat(this.timerDuration) * 60 - 45;
      }, 1000);
    }

    updateTimer() {
      const pad = (value) => String(value).padStart(2, '0');
      if (this.minutesSpan) this.minutesSpan.innerText = pad(Math.floor(this.totalSeconds / 60));
      if (this.secondsSpan) this.secondsSpan.innerText = pad(this.totalSeconds % 60);
    }

    closeModal() {
      this.modal.classList.remove('popup-modal--active');
      this.overlay?.classList.remove('popup-overlay--active');
    }
  }
  customElements.define('promo-popup', PromoPopup);

  /* ---------- Comparison slider ---------- */

  class ComparisonSlider extends HTMLElement {
    constructor() {
      super();
      this.sliderOverlay = this.querySelector('.comparison-slider__overlay');
      this.sliderLine = this.querySelector('.comparison-slider__line');
      this.sliderInput = this.querySelector('.comparison-slider__input');
      this.sliderInput?.addEventListener('input', (event) => {
        const value = event.currentTarget.value;
        this.sliderOverlay.style.width = `${value}%`;
        this.sliderLine.style.left = `${value}%`;
      });
    }
  }
  customElements.define('comparison-slider', ComparisonSlider);

  /* ---------- Section group (two sections side by side) ---------- */

  class SectionsGroup extends HTMLElement {
    constructor() {
      super();
      this.sectionOneContainer = this.querySelector('.section-group__section-one-container');
      this.sectionTwoContainer = this.querySelector('.section-group__section-two-container');
      this.transferSections();
      document.addEventListener('shopify:section:load', this.transferSections.bind(this));
    }

    transferSections() {
      this.sectionOne = document.querySelector(`${this.dataset.sectionOneId} .content-for-grouping`);
      this.sectionTwo = document.querySelector(`${this.dataset.sectionTwoId} .content-for-grouping`);
      if (this.sectionOne && !this.sectionOneContainer.childNodes.length) this.sectionOneContainer.appendChild(this.sectionOne);
      if (this.sectionTwo && !this.sectionTwoContainer.childNodes.length) this.sectionTwoContainer.appendChild(this.sectionTwo);
    }
  }
  customElements.define('section-group', SectionsGroup);

  /* ---------- Product recommendations (related, complementary, upsells) ---------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          if (this.dataset.loaded !== 'true') this.loadProducts();
        },
        { rootMargin: '0px 0px 400px 0px' }
      ).observe(this);
    }

    loadProducts() {
      if (this.dataset.loaded === 'true' || !this.dataset.url) return;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const container = document.createElement('div');
          container.innerHTML = text;
          const recommendations = container.querySelector('sh-product-recommendations');
          let markup = '';
          if (recommendations && recommendations.innerHTML.trim().length) {
            markup = recommendations.innerHTML;
            const isUpsellBlock = this.dataset.isUpsellBlock === 'true';
            if (isUpsellBlock && !markup.includes('-upsell') && !markup.includes('data-selected')) markup = '';
          }
          if (markup.trim().length) {
            this.innerHTML = markup;
            if (this.dataset.isUpsellBlock === 'true') {
              if (recommendations.classList.contains('upsells-container--stacked-columns')) {
                this.classList.add('upsells-container--stacked-columns');
              }
              if (recommendations.classList.contains('side-margins-negative')) this.classList.add('side-margins-negative');
              this.dataset.count = recommendations.dataset.count;
              this.style.setProperty('--item-count', recommendations.dataset.count);
            }
          }
          if (!this.querySelector('sh-slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }
          if (container.querySelector('.grid__item')) this.classList.add('product-recommendations--loaded');
          this.dataset.loaded = 'true';
        })
        .catch((e) => console.error(e));
    }
  }
  customElements.define('sh-product-recommendations', ProductRecommendations);
})();
